import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import {
  Button,
  Container,
  Grid,
  Header,
  Icon,
  Label,
  Menu,
  Segment,
} from 'semantic-ui-react';
import moment from 'moment-timezone';
import { withTranslation } from 'react-i18next';
import { fetchM } from '../../actions/fetchM';
import { flashErrorMessage, flashSuccessMessage } from '../../actions/flash';
import { compress, dataURLToBlob } from '../../utils/compress';
import EditImage from '../../components/EditImage';
import LoadImage from '../../components/LoadImage';
import ESigningModal from '../../components/SignaturePad/ESigningModal';
import ConsentTemplate from '../../components/CreditCheckConsentTemplate';
import { namespaces, TRANSLATION_FILES } from '../../constants/translation';

const DEFAULT_ACCEPT = 'image/jpeg, image/png, application/pdf';

// Whether a file is one of the types in an accept list such as 'image/jpeg, image/png', which may
// also hold wildcards ('image/*') and extensions ('.pdf').
const fileAccepted = (accept, file) => (accept || '').split(',')
  .map(type => type.trim().toLowerCase())
  .filter(Boolean)
  .some((type) => {
    if (type.startsWith('.')) return (file.name || '').toLowerCase().endsWith(type);
    if (type.endsWith('/*')) return (file.type || '').toLowerCase().startsWith(type.slice(0, -1));
    return (file.type || '').toLowerCase() === type;
  });

class UploadForm extends Component {
  static propTypes = {
    as: PropTypes.string,
    customerID: PropTypes.string,
    jobTitle: PropTypes.string,
    customerName: PropTypes.string,
    identity: PropTypes.string,
    identityTypeID: PropTypes.number,
    isCompress: PropTypes.bool,
    title: PropTypes.string,
    picture: PropTypes.shape({
      key: PropTypes.string,
      tags: PropTypes.arrayOf(
        PropTypes.string,
      ),
    }),
    auth: PropTypes.shape({
      company: PropTypes.object,
    }),
    onChange: PropTypes.func,
    signature: PropTypes.bool,
    sampleURL: PropTypes.string,
    sampleSize: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    labelContent: PropTypes.string,
    loading: PropTypes.bool,
    downloadURL: PropTypes.string,
    signedAt: PropTypes.string,
    onSigned: PropTypes.func,
    bucketName: PropTypes.string,
    // A ready URL for the current picture, used instead of building one from picture.key
    // (CPI-1332: a linked purchaser's CRM Customer identity picture, loaded from book-api).
    pictureSrc: PropTypes.string,
    // The file types offered (CPI-1332: a linked purchaser's CRM Customer picture must be an
    // image).
    accept: PropTypes.string,
    t: PropTypes.func,
  }
  static defaultProps = {
    as: 'form',
    accept: DEFAULT_ACCEPT,
  }
  static contextTypes = {
    store: PropTypes.object,
  }
  static defaultValues = {
    file: null,
    name: '',
    src: '',
    type: '',
    tags: [],
    degree: 0,
  }
  static defaultPicture = {
    createdAt: '',
    customerID: '',
    id: '',
    key: '',
    name: '',
    size: 0,
    tags: [],
    updatedAt: '',
    userID: 0,
  }
  static getPropsToState = (props) => {
    const picture = Object.assign({}, UploadForm.defaultPicture, props.picture || {});
    return {
      picture,
    };
  }
  constructor(props) {
    super(props);
    const upload = Object.assign({}, UploadForm.defaultValues);
    const state = UploadForm.getPropsToState(props);
    this.state = {
      upload,
      ...state,
      accepted: [],
      rejected: [],
      isSignature: true,
      loading: false,
      isSigned: false,
      files: '',
    };
  }
  getFormData() {
    return new FormData(this.form);
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => {
        res(loading);
      });
    });
  }
  setSigned(isSigned = false) {
    return new Promise((res) => {
      this.setState({ isSigned }, () => {
        res(isSigned);
      });
    });
  }
  getDocumentToken(callback) {
    const { store } = this.context;
    store.dispatch(fetchM(`/customers/${this.props.customerID}/documents/`, {
      credentials: 'include',
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }))
      .catch((err) => {
        store.dispatch(flashErrorMessage(err));
      })
      .then((res) => {
        const documentToken = res.headers.get('X-Csrf-Token');
        this.setState({
          documentToken,
        }, () => {
          if (callback) {
            callback();
          }
        });
      });
  }
  resetFiles() {
    // temporary fix for upload modal not closing
    this.setState({
      upload: Object.assign({}, UploadForm.defaultValues),
    }, () => {
      this.handleOnChange();
    });
  }
  handleOnChange() {
    if (this.props.onChange) {
      const { upload } = this.state;
      this.props.onChange(upload, upload.src.length > 0);
    }
  }
  rejectedMessage() {
    const { accept } = this.props;
    return accept === DEFAULT_ACCEPT ? 'Only image/jpeg, image/png, and application/pdf accepted' : `Only ${accept} accepted`;
  }
  handleChangeFile = async () => {
    await this.setLoading(true);
    const { picture } = this.state;
    const files = Array.prototype.slice.call(this.uploadFiles.files);
    this.uploadFiles.value = null;
    if (window.FileReader && files && files.length > 0) {
      const file = files[0];
      // CPI-1426: the picker's accept is only a hint ("All files" bypasses it), so a picked file
      // is checked when the caller narrowed the types. The default is left to book-api as before,
      // which also takes types the default does not list (GIF).
      const { accept } = this.props;
      if (file && accept !== DEFAULT_ACCEPT && !fileAccepted(accept, file)) {
        this.context.store.dispatch(flashErrorMessage({ message: this.rejectedMessage() }));
        await this.setLoading(false);
        return;
      }
      if (file) {
        const reader = new FileReader();
        if (file.type.match('image.*') && this.props.isCompress && file.size > 1048576) {
          await compress(file)
            .then((b) => {
              reader.onloadend = () => {
                const upload = Object.assign(this.state.upload, {
                  file: b,
                  name: file.name,
                  src: reader.result,
                  type: file.type,
                  tags: [].concat(this.props.tags) || [],
                  degree: 0,
                });
                this.setState({ upload }, () => {
                  this.handleOnChange();
                });
                const newPicture = Object.assign({}, picture);
                newPicture.key = reader.result;
                newPicture.name = file.name;
                this.setState({ picture: newPicture });
              };
              reader.readAsDataURL(b);
            });
        } else {
          reader.onloadend = () => {
            const upload = Object.assign(this.state.upload, {
              file,
              name: file.name,
              src: reader.result,
              type: file.type,
              tags: [].concat(this.props.tags) || [],
              degree: 0,
            });
            this.setState({ upload }, () => {
              this.handleOnChange();
            });
            const newPicture = Object.assign({}, picture);
            newPicture.key = reader.result;
            newPicture.name = file.name;
            this.setState({ picture: newPicture });
          };
          reader.readAsDataURL(file);
        }
      }
      await this.setLoading(false);
    }
  }
  handleChangeDropFile = async ({ accepted, rejected }) => {
    await this.setLoading(true);
    this.uploadFiles.value = null;
    const { store } = this.context;
    const { picture } = this.state;
    if (rejected.length > 0) {
      store.dispatch(flashErrorMessage({ message: this.rejectedMessage() }));
    }
    if (window.FileReader && accepted && accepted.length > 0) {
      const file = accepted[0];
      if (file) {
        const reader = new FileReader();
        if (file.type.match('image.*') && this.props.isCompress && file.size > 1048576) {
          await compress(file)
            .then((b) => {
              reader.onloadend = () => {
                const upload = Object.assign(this.state.upload, {
                  file: b,
                  name: file.name,
                  src: reader.result,
                  type: file.type,
                  tags: [].concat(this.props.tags) || [],
                  degree: 0,
                });
                this.setState({ upload }, () => {
                  this.handleOnChange();
                });
                const newPicture = Object.assign({}, picture);
                newPicture.key = reader.result;
                newPicture.name = file.name;
                this.setState({ picture: newPicture });
              };
              reader.readAsDataURL(b);
            });
        } else {
          reader.onloadend = () => {
            const upload = Object.assign(this.state.upload, {
              file,
              name: file.name,
              src: reader.result,
              type: file.type,
              tags: [].concat(this.props.tags) || [],
              degree: 0,
            });
            this.setState({ upload }, () => {
              this.handleOnChange();
            });
            const newPicture = Object.assign({}, picture);
            newPicture.key = reader.result;
            newPicture.name = file.name;
            this.setState({ picture: newPicture });
          };
          reader.readAsDataURL(file);
        }
      }
      await this.setLoading(false);
    }
  }
  handleRemove = (event) => {
    event.preventDefault();
    const upload = Object.assign({}, UploadForm.defaultValues);
    const newPicture = Object.assign({}, this.state.picture);
    newPicture.key = '';
    newPicture.name = '';
    this.setState({ picture: newPicture });
    this.setState({ upload }, () => {
      this.handleOnChange();
    });
  }
  handleAddFile = (event) => {
    event.preventDefault();
    this.uploadFiles.click();
  }
  async generateSignature(body) {
    await this.setLoading(true);
    const { picture } = this.state;
    const { store } = this.context;
    let res;
    try {
      res = await store.dispatch(fetchM(`/customers/${this.props.customerID}/documents/signatures/`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          // Let browser decide Content-Type
          'X-Csrf-Token': this.state.documentToken,
        },
        body,
      }));
    } catch (err) {
      await this.setLoading(false);
      Bugsnag.notifyException(err);
      if (err.res && err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
        return;
      }
      store.dispatch(flashErrorMessage({ message: 'There was an error with your request. Please try again.' }));
      return;
    }
    if (res.ok) {
      const response = await res.json();
      const newPicture = Object.assign({}, picture);
      newPicture.key = response.key;
      newPicture.name = response.name;
      this.setState({ picture: newPicture });
    }
    await this.setLoading(false);
    await this.signatureModal.handleOnCancel();
    await this.setSigned(true);
    this.props.onSigned();
    store.dispatch(flashSuccessMessage({ header: 'Consent signed', message: 'Consent signed and generated' }));
  }
  saveSignature = async (event) => {
    event.preventDefault();
    const { store } = this.context;
    if (this.signatureModal.isEmpty()) {
      store.dispatch(flashErrorMessage({ message: 'Please sign the consent' }));
      return;
    }

    const formData = new FormData();

    formData.append('signature', dataURLToBlob(this.signatureModal.signData()));
    if (formData !== null) {
      await this.getDocumentToken(() => {
        this.generateSignature(formData);
      });
    }
  }
  handleRotate = (direction) => {
    const { upload } = this.state;
    if (!upload.degree) {
      upload.degree = 0;
    }
    const increment = direction === 'right' ? 1 : -1;
    upload.degree += 90 * increment;
    if (upload.degree >= 360) {
      upload.degree = 0;
    }
    if (upload.degree < 0) {
      upload.degree = 270;
    }
    this.setState({ upload }, () => {
      this.handleOnChange();
    });
  }
  tabs = [
    { name: 'isSignature', text: 'E-signing', description: '(Required touch screen)', isSignature: true },
    { name: 'isUpload', text: 'Upload Form', description: '(10mb max)', isSignature: false },
  ];
  handleChangeTab = (signature = true) => {
    let { isSignature } = this.state;
    isSignature = signature;
    this.setState({ isSignature });
  }
  handleOpenConsentForm = (e) => {
    e.preventDefault();
    window.open(this.props.downloadURL);
  }
  render() {
    const { t } = this.props;
    const { as, title, labelContent } = this.props;
    const { isSignature, isSigned, picture, upload } = this.state;
    const Wrapper = as;
    return (
      <Wrapper
        encType="multipart/form-data"
        name="uploadForm"
        ref={(ref) => { this.form = ref; }}
      >
        <input
          ref={(ref) => { this.uploadFiles = ref; }}
          type="file"
          onChange={this.handleChangeFile}
          accept={this.props.accept}
          style={{ display: 'none' }}
        />
        {
          this.props.signature &&
          <Grid>
            <Grid.Row centered>
              <Grid.Column
                computer={title === 'NewCreditForm' ? 8 : null}
                tablet={title === 'NewCreditForm' ? 10 : null}
                mobile={title === 'NewCreditForm' ? 16 : null}
              >
                <h3
                  style={{
                    textAlign: 'left',
                  }}
                >
                  Choose One
                </h3>
                <div className="menuTab">
                  <Menu fluid widths={2} >
                    {
                    this.tabs.map((tab, idx) => (
                      <Menu.Item
                        key={idx}
                        name={tab.name}
                        onClick={() => this.handleChangeTab(tab.isSignature)}
                        active={isSignature === tab.isSignature}
                      >
                        <div>
                          <p style={{ marginBottom: 0 }}>{tab.text}</p>
                          <p style={{ fontSize: '10px' }}>{tab.description}</p>
                        </div>
                      </Menu.Item>
                    ))
                  }
                  </Menu>
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        }
        {
          !isSignature || !this.props.signature ?
            <Grid columns="equal" textAlign="center">
              {
                this.state.picture.name && (title === 'NewCreditForm' || title === 'Front' || title === 'Back') ? (
                  <Grid.Row style={{ paddingBottom: 0 }}>
                    <Grid.Column
                      computer={title === 'NewCreditForm' ? 5 : null}
                      tablet={title === 'NewCreditForm' ? 6 : null}
                      mobile={title === 'NewCreditForm' ? 8 : null}
                      style={{ textAlign: 'left', width: title !== 'NewCreditForm' ? '60%' : null, overflow: 'hidden' }}
                    >
                      <Icon name="check circle outline" style={{ color: 'rgba(0, 151, 161, 1)' }} />
                      {this.state.picture.name}
                    </Grid.Column>
                    <Grid.Column
                      computer={title === 'NewCreditForm' ? 3 : null}
                      tablet={title === 'NewCreditForm' ? 4 : null}
                      mobile={title === 'NewCreditForm' ? 8 : null}
                      style={{ textAlign: 'right', width: title !== 'NewCreditForm' ? '40%' : null }}
                    >
                      <div className="replaceButton">
                        <Button
                          floated="right"
                          onClick={this.handleAddFile}
                          content={t('Replace file', { ns: TRANSLATION_FILES.translation })}
                        />
                      </div>
                    </Grid.Column>
                  </Grid.Row>
                ) : null
              }
              <Grid.Row style={{ padding: title === 'FinancialForm' ? 0 : null }}>
                <Grid.Column>
                  {
                    upload.file && upload.type !== 'application/pdf' && this.state.picture.name !== 'consent.png' && title !== 'FinancialForm' ? (
                      <Label
                        style={{ opacity: 0.75 }}
                        as="a"
                        attached="top left"
                        icon={{ name: 'repeat', flipped: 'horizontally' }}
                        disabled={this.props.loading}
                        onClick={() => this.handleRotate('left')}
                      />
                    ) : null
                  }
                  {
                    upload.file && upload.type !== 'application/pdf' && this.state.picture.name !== 'consent.png' && title !== 'FinancialForm' ? (
                      <Label
                        style={{ opacity: 0.75 }}
                        as="a"
                        attached="top right"
                        icon="repeat"
                        disabled={this.props.loading}
                        onClick={() => this.handleRotate('right')}
                      />
                    ) : null
                  }
                  {
                    upload.file && upload.type !== 'application/pdf' && this.state.picture.name !== 'consent.png' && title !== 'FinancialForm' ? (
                      <EditImage
                        src={picture.key}
                        degree={upload.degree || 0}
                        onClick={this.handleAddFile}
                      />
                    ) : null
                  }
                  {
                    !upload.file && upload.type === 'application/pdf' && title !== 'FinancialForm' && title !== 'NewCreditForm' && title !== 'Front' && title !== 'Back' ? (
                      <LoadImage
                        centered
                        onClick={this.handleAddFile}
                        picture={this.props.picture}
                        pictureSize={this.props.picture ? 70 : null}
                        src={this.props.sampleURL || ''}
                        size={this.props.picture ? null : this.props.sampleSize || 'medium'}
                        bucketName={this.props.bucketName}
                      />
                    ) : null
                  }
                  {
                    upload.file && upload.type !== 'application/pdf' && this.state.picture.name === 'consent.png' && title !== 'FinancialForm' ? (
                      <LoadImage
                        centered
                        onClick={this.handleAddFile}
                        picture={this.state.picture}
                        pictureSize={this.state.picture ? 70 : null}
                        src={this.props.sampleURL || ''}
                        size={this.state.picture ? null : this.props.sampleSize || 'medium'}
                        bucketName={this.props.bucketName}
                      />
                    ) : null
                  }
                  {
                    !upload.file && this.props.pictureSrc && title !== 'FinancialForm' ? (
                      <LoadImage
                        centered
                        onClick={this.handleAddFile}
                        picture={null}
                        src={this.props.pictureSrc}
                      />
                    ) : null
                  }
                  {
                    !upload.file && !this.props.pictureSrc && this.state.picture.key && title !== 'FinancialForm' ? (
                      <LoadImage
                        centered
                        onClick={this.handleAddFile}
                        picture={this.state.picture}
                        pictureSize={this.state.picture ? 70 : null}
                        src={this.props.sampleURL || ''}
                        size={this.state.picture ? null : this.props.sampleSize || 'medium'}
                        bucketName={this.props.bucketName}
                      />
                    ) : null
                  }
                  {
                    (upload.file || this.state.picture.key) && title === 'FinancialForm' ? (
                      <Grid>
                        <Grid.Row style={{ marginTop: '1rem', paddingBottom: 0 }}>
                          <Grid.Column>
                            <Header
                              as="h5"
                              style={{ marginBottom: '0.5em', width: '60%', float: 'left', marginTop: '0.5rem' }}
                              textAlign="left"
                              content={labelContent}
                            />
                            <div className="replaceButton" style={{ width: '40%', float: 'right' }}>
                              <Button
                                floated="right"
                                onClick={this.handleAddFile}
                                content={t('Replace file', { ns: TRANSLATION_FILES.translation })}
                              />
                            </div>
                          </Grid.Column>
                        </Grid.Row>
                        <Grid.Row style={{ paddingTop: 0 }}>
                          <Grid.Column>
                            <Container style={{ border: '1px solid rgb(217, 218, 220)', padding: '1rem', borderRadius: '0.25rem', background: this.state.picture.name ? 'rgba(231, 236, 241)' : 'transparent', height: '50px', overflow: 'hidden' }}>
                              <Icon name="file outline" style={{ float: 'left' }} />
                              <p style={{ float: 'left', width: '70%' }}>{this.state.picture.name}</p>
                            </Container>
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    ) : null
                  }
                </Grid.Column>
              </Grid.Row>
              {
                this.props.downloadURL ? (
                  <Grid.Row style={{ padding: '1rem 0' }}>
                    <Grid.Column>
                      <Button
                        className="consentLink"
                        content="Download Consent Authorization Form"
                        onClick={e => this.handleOpenConsentForm(e)}
                      />
                    </Grid.Column>
                  </Grid.Row>
              ) : null
            }
              {
              title === 'NewCreditForm' || title === 'FinancialForm' || title === 'Front' || title === 'Back' ? (
                <Grid.Row centered>
                  <Grid.Column computer={title === 'NewCreditForm' ? 8 : null} tablet={title === 'NewCreditForm' ? 10 : null} mobile={title === 'NewCreditForm' ? 16 : null}>
                    {
                      !upload.file && this.state.picture.key === '' && title === 'FinancialForm' ? (
                        <Header
                          as="h5"
                          style={{ marginBottom: '0.5em', width: '60%', float: 'left', marginTop: '0.5rem' }}
                          textAlign="left"
                          content={labelContent}
                        />
                      ) : null
                    }
                    {
                      (!upload.file && this.state.picture.key === '' && !this.props.pictureSrc) ?
                        (
                          <Dropzone
                            accept={this.props.accept}
                            onDrop={(accepted, rejected) => { this.handleChangeDropFile({ accepted, rejected }); }}
                            style={title === 'FinancialForm' ? {
                              width: '100%',
                              height: '40px',
                              borderColor: 'rgba(164, 179, 193, 1)',
                              borderRadius: '0.3rem',
                              borderStyle: 'dashed',
                              borderWidth: '1px',
                              display: 'flex',
                              alignItems: 'center',
                            } : {
                              width: '100%',
                              height: '200px',
                              background: 'rgba(229, 244, 245, 1)',
                              borderColor: 'rgba(0, 151, 161, 1)',
                              borderRadius: '0.3rem',
                              borderStyle: 'dashed',
                              borderWidth: '1px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                            activeStyle={{ background: 'rgba(0, 151, 161, 1)' }}
                          >
                            {
                              ({ isDragActive }) => (
                                <Header
                                  as="a"
                                  style={
                                    title === 'FinancialForm' ? {
                                      textAlign: 'center',
                                      width: '100%',
                                      height: '100%',
                                      marginTop: '2rem',
                                    } : null}
                                >
                                  {
                                    (title === 'NewCreditForm' || title === 'Front' || title === 'Back') && !isDragActive ? (
                                      <div style={{ color: 'rgba(0, 151, 161, 1)' }}>
                                        <Icon name="plus circle" />
                                        <Header.Content>
                                          {
                                            title === 'NewCreditForm' ? 'Upload signed form' : null
                                          }
                                          {
                                            title === 'Front' ? t('Upload Front') : null
                                          }
                                          {
                                            title === 'Back' ? t('Upload Back') : null
                                          }
                                        </Header.Content>
                                      </div>
                                    ) : null
                                  }
                                  {
                                    (title === 'NewCreditForm' || title === 'Front' || title === 'Back') && isDragActive ? (
                                      <div style={{ color: 'white' }}>
                                        <Header.Content>
                                          Drop file here
                                          <Header.Subheader style={{ color: 'white' }}>
                                            Add file by dropping them in this window
                                          </Header.Subheader>
                                        </Header.Content>
                                      </div>
                                    ) : null
                                  }
                                  {
                                    title === 'FinancialForm' && !isDragActive ? (
                                      <div style={{ color: 'rgba(89, 105, 117, 1)' }}>
                                        <Header.Content style={{ fontWeight: 'normal' }}>
                                          + Upload file
                                        </Header.Content>
                                      </div>
                                    ) : null
                                  }
                                  {
                                    title === 'FinancialForm' && isDragActive ? (
                                      <div style={{ color: 'white' }}>
                                        <Header.Content>
                                          Drop file here
                                        </Header.Content>
                                      </div>
                                    ) : null
                                  }
                                </Header>
                              )
                            }
                          </Dropzone>
                      ) : null
                    }
                  </Grid.Column>
                </Grid.Row>
              ) : (
                <Grid.Row>
                  <Grid.Column>
                    {
                      !upload.file ?
                        <Button
                          fluid
                          primary
                          disabled={this.props.loading}
                          onClick={this.handleAddFile}
                        >
                          <i className="icon">
                            <img src="/assets/images/icons/upload.svg" alt="" />
                          </i>
                          Upload File
                        </Button> :
                      null
                    }
                  </Grid.Column>
                </Grid.Row>
              )
            }
            </Grid> :
            <Segment style={{ boxShadow: '0 0 5px 0 rgba(0, 0, 0, 0.25)' }}>
              <Grid className="signature-upload" columns="equal">
                {
                  isSigned && picture.name === 'consent.png' ?
                    <Grid.Row textAlign="center">
                      <Grid.Column>
                        <LoadImage
                          centered
                          picture={picture}
                          pictureSize={this.state.picture ? 70 : null}
                          src={this.props.sampleURL || ''}
                          size={this.state.picture ? null : this.props.sampleSize || 'medium'}
                          bucketName={this.props.bucketName}
                        />
                      </Grid.Column>
                    </Grid.Row>
                    :
                    <Grid.Row>
                      <Grid.Column>
                        <ConsentTemplate
                          company={this.props.auth && this.props.auth.company}
                          customerName={this.props.customerName}
                          jobTitle={this.props.jobTitle}
                          identityTypeID={this.props.identityTypeID}
                          identity={this.props.identity}
                          customerID={this.props.customerID}
                        />
                      </Grid.Column>
                    </Grid.Row>
                }
                {
                  ((this.props.signedAt && this.props.signedAt !== null &&
                    moment().isBefore(moment(this.props.signedAt).add(1, 'day'))) ||
                  this.state.isSigned) &&
                  <Grid.Row className="sign">
                    <Grid.Column>
                      <div className="round icon">
                        <img alt="e-signing" src="/assets/images/icons/tick-white.svg" />
                      </div>
                    </Grid.Column>
                    <Grid.Column>
                      <p>E-signing</p>
                    </Grid.Column>
                  </Grid.Row>
                }
                <Grid.Row centered style={{ background: 'rgba(229, 244, 245, 1)', padding: '2rem' }}>
                  <Grid.Column className="res-width">
                    <ESigningModal
                      ref={(ref) => { this.signatureModal = ref; }}
                      loading={this.state.loading}
                      saveButton={
                        <Button onClick={this.saveSignature} disabled={this.state.loading} positive color="green" content="Save" />
                      }
                      trigger={
                        this.state.isSigned ?
                          <div className="clickable link">
                            Sign again
                          </div> :
                          <div>
                            <div className="eSignButton">
                              <Button
                                disabled={this.state.loading}
                              >
                                E-sign here
                              </Button>
                            </div>
                          </div>
                      }
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Segment>
          }
      </Wrapper>
    );
  }
}

export default withTranslation(namespaces, { withRef: true })(UploadForm);



// WEBPACK FOOTER //
// ./src/containers/CreditCheck/UploadForm.jsx